/**
 * Wi-Fi users (formerly "Network Users"; Customer > Configure) — the people who CONNECT. Not the people who administer.
 *
 * Meraki draws this line and Meridian was missing one side of it: Organization >
 * Administrators is the Operators screen; Organization > Users is this one. Residents,
 * staff and contractors who need to get on the WiFi — not dashboard logins.
 *
 * Four things shape the design, all of them consequences of one fact: a network user is
 * deliberately NOT confined to a single customer.
 *
 *  1. ACCESS IS A LIST, NOT A FIELD. One person can hold access at several customers and
 *     several sites. The form edits access AT THIS CUSTOMER; access elsewhere is not
 *     shown and is not touched. That is enforced server-side too — this screen cannot
 *     revoke what it never displayed.
 *  2. "EVERY BUILDING" IS ITS OWN STATE. It is not "all the boxes happen to be ticked":
 *     it keeps covering sites added next year. The picker says which one is in force,
 *     because the difference only shows up months later when a new site appears.
 *  3. SUSPEND AND DELETE ARE NOT SCOPED. They block the person everywhere they work. When
 *     someone holds access the caller cannot see (`fully_visible: false`) the server
 *     refuses, so those controls are disabled here WITH the reason — a click that returns
 *     403 teaches nothing.
 *  4. AN EXISTING EMAIL IS NOT A COLLISION. Adding someone who already exists at another
 *     customer grants them access here; it does not fail, and it does not disclose where
 *     else they work.
 *
 * Vocabulary: "people", "customers", "sites". Never realms, grants, tenancy or scope.
 */
import { useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import {
  useNetworkUsers,
  useCreateNetworkUser,
  useUpdateNetworkUser,
  useDeleteNetworkUser,
  type NetworkUserBody,
} from "@/lib/api/hooks";
import { PageHeader, ErrorBanner, EmptyState } from "@/components/ui/Page";
import { Button } from "@/components/ui/Button";
import { RoleGate } from "@/components/auth/RoleGate";
import { useSession } from "@/stores/session";
import type { NetworkUser } from "@/types/domain";

const ACTIVE = "active";
const SUSPENDED = "suspended";

/** Sites in the customer currently in scope. */
function useScopeSites() {
  // See ScopeBar: never select a derived array from the store — it is a fresh object each
  // render and Zustand's Object.is check then re-renders forever (React #185).
  const me = useSession((s) => s.me);
  const currentOrgId = useSession((s) => s.currentOrgId);
  return useMemo(
    () => (me ? me.sites.filter((s) => String(s.org_id) === String(currentOrgId)) : []),
    [me, currentOrgId],
  );
}

/** The org id the screen writes against, as a number (the store carries it as a string). */
function useScopeOrgId(): number | null {
  const currentOrgId = useSession((s) => s.currentOrgId);
  return currentOrgId == null ? null : Number(currentOrgId);
}

export default function NetworkUsers() {
  const [search, setSearch] = useState("");
  const { data, isLoading, error } = useNetworkUsers(search.trim() || undefined);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<NetworkUser | null>(null);
  const [removing, setRemoving] = useState<NetworkUser | null>(null);

  const people = data?.items ?? [];

  return (
    <div>
      <div className="flex items-start justify-between gap-3">
        <PageHeader
          title="Wi-Fi users"
          subtitle="People who connect to the WiFi, and which sites they can use"
        />
        <RoleGate min="site_operator" mode="hide">
          <Button variant="primary" onClick={() => setAdding(true)}>
            Add person
          </Button>
        </RoleGate>
      </div>

      {error && <ErrorBanner error={error} />}

      <div className="mb-4 mt-2">
        <label className="sr-only" htmlFor="nu-search">
          Search people
        </label>
        <input
          id="nu-search"
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by email or name"
          className="w-full max-w-sm rounded border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-subtle"
        />
      </div>

      {isLoading && <div className="text-sm text-ink-muted">Loading people…</div>}

      {data && people.length === 0 && (
        <EmptyState
          title={search ? "Nobody matches that" : "No Wi-Fi users yet"}
          hint={
            search
              ? "Try part of their email address."
              : "Add the people who need to get on the WiFi. Each one gets access to the sites you choose — and only those."
          }
        />
      )}

      {people.length > 0 && (
        <ul className="flex flex-col gap-2">
          {people.map((person) => (
            <PersonRow
              key={person.id}
              person={person}
              onEdit={() => setEditing(person)}
              onRemove={() => setRemoving(person)}
            />
          ))}
        </ul>
      )}

      {adding && <PersonDialog onClose={() => setAdding(false)} />}
      {editing && <PersonDialog person={editing} onClose={() => setEditing(null)} />}
      {removing && <RemoveDialog person={removing} onClose={() => setRemoving(null)} />}
    </div>
  );
}

/* ------------------------------------------------------------------------------------- */

function PersonRow({
  person,
  onEdit,
  onRemove,
}: {
  person: NetworkUser;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const sites = useScopeSites();
  const orgId = useScopeOrgId();
  const update = useUpdateNetworkUser();

  const grant = person.grants.find((g) => g.org_id === orgId);
  const suspended = person.state === SUSPENDED;

  const buildingLabel = useMemo(() => {
    if (!grant) return "No sites";
    if (grant.all_sites) return "Every site";
    if (grant.site_ids.length === 0) return "No sites";
    const names = grant.site_ids
      .map((id) => sites.find((s) => String(s.id) === String(id))?.name ?? `Site ${id}`)
      .sort();
    return names.length <= 2
      ? names.join(", ")
      : `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
  }, [grant, sites]);

  /* Access to nothing is the alarming state: they exist, they cannot connect, and to them
     the network simply looks broken. Everything else here is descriptive and stays neutral. */
  const noReach = !grant || (!grant.all_sites && grant.site_ids.length === 0);

  return (
    <li className="md-card flex flex-wrap items-center gap-3 p-3">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate font-medium text-ink">
            {person.display_name || person.email}
          </span>
          {suspended && (
            <span className="rounded border border-line px-1.5 py-0.5 text-[0.625rem] uppercase tracking-wide text-ink-subtle">
              suspended
            </span>
          )}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-muted">
          {person.display_name && <span className="truncate">{person.email}</span>}
          {person.display_name && (
            <span aria-hidden className="text-ink-subtle">
              ·
            </span>
          )}
          <span className={noReach ? "text-warn" : undefined}>{buildingLabel}</span>
        </div>
      </div>

      <RoleGate min="site_operator" mode="hide">
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={onEdit}>
            Edit access
          </Button>
          {/* Rule 3: suspend reaches every customer this person works at, so it is only
              offered when this caller can see all of them. */}
          <Button
            variant="ghost"
            disabled={!person.fully_visible || update.isPending}
            title={
              person.fully_visible
                ? undefined
                : "This person also has access at a customer you cannot see. Suspending them would block them there too."
            }
            onClick={() =>
              update.mutate({ id: person.id, state: suspended ? ACTIVE : SUSPENDED })
            }
          >
            {suspended ? "Reactivate" : "Suspend"}
          </Button>
          <Button
            variant="ghost"
            disabled={!person.fully_visible}
            title={
              person.fully_visible
                ? undefined
                : "This person also has access at a customer you cannot see, so removing them here is not possible."
            }
            onClick={onRemove}
          >
            Remove
          </Button>
        </div>
      </RoleGate>
    </li>
  );
}

/* ------------------------------------------------------------------------------------- */

function PersonDialog({ person, onClose }: { person?: NetworkUser; onClose: () => void }) {
  const editingExisting = !!person;
  const sites = useScopeSites();
  const orgId = useScopeOrgId();
  const myRole = useSession((s) => s.effectiveRole());

  const create = useCreateNetworkUser();
  const update = useUpdateNetworkUser();

  /* Only org_admin and above may grant "every site" — below that a manager holds
     specific sites and can hand out only those. Offering the option to someone who
     cannot use it turns a rule into a rejection. */
  const mayGrantAllSites = myRole === "org_admin" || myRole === "msp_admin";

  const existingGrant = person?.grants.find((g) => g.org_id === orgId);

  const [email, setEmail] = useState(person?.email ?? "");
  const [displayName, setDisplayName] = useState(person?.display_name ?? "");
  const [allSites, setAllSites] = useState(
    existingGrant ? existingGrant.all_sites : mayGrantAllSites,
  );
  const [siteIds, setSiteIds] = useState<number[]>(existingGrant?.site_ids ?? []);

  const pending = create.isPending || update.isPending;
  const failure = (create.error ?? update.error) as Error | null;

  const effectiveAllSites = allSites && mayGrantAllSites;
  const nothingChosen = !effectiveAllSites && siteIds.length === 0;

  function toggleSite(id: number) {
    setSiteIds((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (orgId == null) return;

    // Access at OTHER customers is deliberately absent from this payload. The server keeps
    // what it did not show us; sending the whole person's access from a form scoped to one
    // customer is exactly how the other customer's access would get wiped.
    const grants: NetworkUserBody["grants"] = [
      effectiveAllSites
        ? { org_id: orgId, all_sites: true }
        : { org_id: orgId, site_ids: siteIds },
    ];

    const body: NetworkUserBody = editingExisting
      ? { display_name: displayName || null, grants }
      : { email, display_name: displayName || null, grants };

    const action = editingExisting
      ? update.mutateAsync({ id: person!.id, ...body })
      : create.mutateAsync(body);
    action.then(onClose).catch(() => undefined);
  }

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40" />
        <Dialog.Content className="md-dialog fixed left-1/2 top-1/2 max-h-[85vh] w-[min(34rem,92vw)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-line bg-surface p-5 shadow-lg">
          <Dialog.Title className="text-base font-medium text-ink">
            {editingExisting ? `Access for ${person!.email}` : "Add person"}
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-ink-muted">
            {editingExisting
              ? "Choose which sites they can use here. Access at other customers is not shown and is not changed."
              : "They will be able to connect in the sites you choose."}
          </Dialog.Description>

          <form onSubmit={submit} className="mt-4 flex flex-col gap-4">
            {!editingExisting && (
              <div className="flex flex-col gap-1">
                <label htmlFor="nu-email" className="text-sm font-medium text-ink">
                  Email address
                </label>
                <input
                  id="nu-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="rounded border border-line bg-surface-2 px-3 py-2 text-sm text-ink"
                />
                <p className="text-xs text-ink-subtle">
                  This is how they are identified. If they already have access at another
                  customer, this adds access here rather than creating a second account.
                </p>
              </div>
            )}

            <div className="flex flex-col gap-1">
              <label htmlFor="nu-name" className="text-sm font-medium text-ink">
                Name <span className="font-normal text-ink-subtle">(optional)</span>
              </label>
              <input
                id="nu-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="e.g. Unit 402, or Facilities contractor"
                className="rounded border border-line bg-surface-2 px-3 py-2 text-sm text-ink"
              />
            </div>

            <fieldset className="flex flex-col gap-2">
              <legend className="text-sm font-medium text-ink">Sites</legend>

              {mayGrantAllSites && (
                <label className="flex items-start gap-2 text-sm text-ink">
                  <input
                    type="checkbox"
                    checked={allSites}
                    onChange={(e) => setAllSites(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span>
                    Every site
                    {/* Rule 2: the distinction only bites later, so say it now. */}
                    <span className="block text-xs text-ink-subtle">
                      Including sites added in future.
                    </span>
                  </span>
                </label>
              )}

              {!effectiveAllSites && (
                <div className="flex flex-col gap-1 rounded border border-line p-2">
                  {sites.length === 0 && (
                    <p className="text-xs text-ink-subtle">
                      No sites are available in your access.
                    </p>
                  )}
                  {sites.map((site) => (
                    <label key={site.id} className="flex items-center gap-2 text-sm text-ink">
                      <input
                        type="checkbox"
                        checked={siteIds.includes(Number(site.id))}
                        onChange={() => toggleSite(Number(site.id))}
                      />
                      {site.name}
                    </label>
                  ))}
                </div>
              )}

              {nothingChosen && (
                <p className="text-xs text-warn">
                  With no sites chosen they will not be able to connect anywhere.
                </p>
              )}
            </fieldset>

            {failure && <ErrorBanner error={failure} />}

            <div className="md-dialog-footer flex justify-end gap-2 bg-surface">
              <Button type="button" variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={pending}>
                {pending ? "Saving…" : editingExisting ? "Save access" : "Add person"}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/* ------------------------------------------------------------------------------------- */

function RemoveDialog({ person, onClose }: { person: NetworkUser; onClose: () => void }) {
  const remove = useDeleteNetworkUser();

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40" />
        <Dialog.Content className="md-dialog fixed left-1/2 top-1/2 w-[min(28rem,92vw)] -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-surface p-5 shadow-lg">
          {/* Naming the person is the point of the confirmation: a generic "are you sure"
              confirms the gesture, not the target. */}
          <Dialog.Title className="text-base font-medium text-ink">
            Remove {person.display_name || person.email}?
          </Dialog.Title>
          <Dialog.Description className="mt-2 text-sm text-ink-muted">
            They lose access to every site immediately. Any WiFi password already issued
            to them stays on record and is not revoked — you can remove it separately.
          </Dialog.Description>

          {remove.error && <ErrorBanner error={remove.error as Error} />}

          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={remove.isPending}
              onClick={() =>
                remove
                  .mutateAsync(person.id)
                  .then(onClose)
                  .catch(() => undefined)
              }
            >
              {remove.isPending ? "Removing…" : "Remove"}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
